/* wallJoins.js — CORNER-NODE JOIN SOLVER (wall-engine mandate, Stage W1).
 *
 * WHY THIS EXISTS
 * The mandate ("walls must behave like professional CAD systems") requires that
 * NODES OWN THE JOINS: walls never independently decide corner geometry. This
 * module is that owner. It consumes the topology oracle (getWallGraph — nodes,
 * tee splits, junction kinds) and computes, for every node, the exact join
 * geometry every consumer (wallPolygons → W3 rendering, W2 cleanup) stands on.
 *
 * ONE ALGORITHM FOR ALL ARITIES — THE ANGULAR FAN. At a node n, take every
 * incident wall-end i with outward unit direction d_i (node→wall) and
 * half-thickness h_i = effectiveThickness(w_i)/2, sorted CCW by atan2(d_i).
 * For each CCW-adjacent pair (i, j) the wedge vertex is
 *     v_ij = offsetLineIntersect(n, d_i, +leftPerp(d_i)·h_i,
 *                                    d_j, −leftPerp(d_j)·h_j)
 * — i's LEFT face line meets j's RIGHT face line. v_ij is stored ONCE and
 * referenced from BOTH wall-ends: that shared float64 array IS the
 * vertex-sharing guarantee (no recomputation, no epsilon drift). The classic
 * cases fall out of the fan instead of being special-cased:
 *   L (2 ends)        → the two wedge vertices are the inner + outer mitre points;
 *   mixed-thickness L → per-wall offsets make the seam the segment joining the
 *                       two intersection points (each wall measures its own
 *                       thickness everywhere — 100 mm stays 100 mm by construction);
 *   near-0° wedge     → MITER-LIMIT bevel (ratio 4, the wallAuthor discipline):
 *                       each wall keeps its own plain offset corner and the
 *                       polygon gains one edge to the shared bevel midpoint
 *                       (W3 must render n-gons — contract note);
 *   near-180° pair    → continuation: equal h flows through one shared offset
 *                       point; unequal h butts SQUARE at the transverse line
 *                       through n (thickness change happens AT the node — a
 *                       step, NOT "thicker wall governs", which is a T rule);
 *   T (3 slots)       → wallGraph already T-splits the host (derived,
 *                       wallGraph.js §2), so the tee node carries host dirs
 *                       d, −d plus branch b. The (d→b) and (b→−d) wedges land
 *                       the branch's end vertices ON the host's near face and
 *                       the (−d→d) anti-parallel wedge keeps the far host face
 *                       continuous: HOST FACE WINS, the branch butts flush, the
 *                       host polygon stays one unbroken rectangle. OFF-AXIS
 *                       tees (the graph accepts a branch tip up to jointTol()
 *                       OFF the host centerline, wallGraph.js §2; node.p is the
 *                       tip-cluster centroid, never projected) anchor the
 *                       host-axis face lines at the PROJECTION of node.p onto
 *                       the HOST CENTERLINE — W1-gate QA ruling — so the
 *                       branch cap lands on the host's REAL face for any
 *                       in-tol offset, both signs, any thickness mix (on-axis
 *                       nodes keep the shared node array: byte-identical fan);
 *   X (4 slots)       → pure 4-way fan, four shared wedge vertices; when all
 *                       four are wall-ENDS the caps additionally run through
 *                       the shared node point (PIE wedges / cross seams) so
 *                       the junction core is covered — see the coverage rule
 *                       at the fan below;
 *   5+ way            → the fan handles any N unchanged.
 * Two THROUGH-walls crossing mid-mid have no graph node; segmentCrossings()
 * detects those and rules a deterministic host (longer wall, tie → smaller id
 * lexicographic) — wallPolygons emits the other wall as TWO parts butting the
 * host's faces (two T-butts; closed form, no clipping dep). A crossing is
 * skipped ONLY under TRUE ownership (W1-gate QA ruling): a graph node incident
 * to BOTH walls (endpoint cluster or derived T-split) within jointTol() of the
 * crossing point — that node's fan is then the single owner (no double-trim);
 * everything else emits a record, so there is no dead band at wall ends.
 *
 * [KNOWN-LIMIT] ACUTE-CORNER BEVEL OVERLAP (W1-gate QA ruling: ACCEPTED). At L
 * corners tighter than ~29° interior (MITER_LIMIT_RATIO 4) BOTH wedges bevel
 * and the two wall rectangles DOUBLE-COVER a sliver near the node (full
 * polygon intersection ≈0.042 m² at 15° with 150 mm walls, 1 mm grid; QA's
 * probe quoted ≈0.0283 m² by its own metric — same accepted defect class).
 * This is the deliberate AutoCAD-MITERLIMIT
 * discipline — bounded corners, correct union OUTLINE, interior double-cover
 * accepted; a boolean-clean subtraction is exactly the clipping dep the DEP
 * RULING forbids. Executable decision: the "[KNOWN-LIMIT] acute-corner bevel
 * overlap" pin in wallJoins.test.js — flip only with a QA re-ruling.
 *
 * ARCS (W1 scope): an arc wall participates in a 2-slot fan by its endpoint
 * TANGENT (standard CAD tangent-mitre); its cap becomes the mitre-seam segment
 * whose endpoints are the closed-form line-circle intersections of the seam
 * line with the outer/inner band circles (nearest-to-node roots). If the seam
 * fails to cut both circles inside the band's angular range — or the arc ends
 * at a tee/cross node (arc chords are never T-split, wallGraph.js §2) — the end
 * falls back to the radial cap and is PINNED via ends[k].fallback='radial-cap'.
 *
 * ONE MITRE IMPLEMENTATION: wallAuthor.mitreJoint() is now a thin delegation to
 * offsetLineIntersect() with offA = offB = side·refOffset(mode)·leftPerp — its
 * reversal/bevel/continuation outputs are pinned by wallAuthorAdversarial and
 * preserved exactly. MITER_LIMIT_RATIO moved HERE (single export; wallAuthor
 * re-exports it for compat).
 *
 * DEP RULING: closed form only — no polygon-clipping library. The fan computes
 * the artefacts the mandate demands (per-wall trims, join classification, EXACT
 * shared vertices) which a boolean library cannot; martinez/clipper epsilon
 * jitter would make the vertex-exact pins flaky. Escape hatch: if a later wave
 * needs booleans they enter ONLY behind engine/geomClip.js, invisible to these
 * APIs.
 *
 * EPSILON DISCIPLINE (uniform, exported): EPS_CROSS=1e-9 (parallel tests, the
 * wallAuthor:254 value), EPS_LEN=1e-12 (zero vectors, chainJointCenters:285),
 * VERTEX_EPS=1e-7 m (shared-vertex assertions), AREA_EPS=1e-10 m²,
 * ANGLE_DUP_DEG=0.5 (duplicate fan directions — the generic wedge + miter
 * limit already bevels those; the constant names the contract). Coordinates
 * are never rounded pre-render.
 *
 * MEMOIZATION: getWallJoins is a single-slot content-fingerprint memo exactly
 * like getWallGraph (wallGraph.js:62-74); the key adds per-wall thickness, the
 * live WALL_THICKNESS binding ([LOCK-W-08]) and arc center/radius/ccw (the
 * graph omits them because it reads only chords — polygons depend on the
 * curve). Deterministic under wall-array permutation: the fan sorts by angle.
 *
 * Module order (no cycles): constants → wallGraph → walls → wallJoins → wallAuthor.
 */
import { WALL_THICKNESS } from "./constants";
import { getWallGraph, jointTol } from "./wallGraph";
import { arcSweep } from "./walls";

// Miter limit (AutoCAD MITERLIMIT analogue) — moved from wallAuthor.js (v0.55.1),
// which re-exports it. Ratio 4 ⇒ corners tighter than ~29° interior angle bevel.
export const MITER_LIMIT_RATIO = 4;
export const EPS_CROSS = 1e-9;   // parallel test on unit-direction cross products
export const EPS_LEN = 1e-12;    // zero-vector test
export const VERTEX_EPS = 1e-7;  // m — shared-vertex coincidence
export const AREA_EPS = 1e-10;   // m² — overlap/gap assertions
export const ANGLE_DUP_DEG = 0.5; // ° — duplicate fan-direction contract
export const ANCHOR_EPS = 1e-9;   // m — a wall-end within this of node.p keeps the SHARED node array as its face anchor

const COLLINEAR_DEG = 2; // continuation classification — mirrors wallGraph.js

const sub = (a, b) => [a[0] - b[0], a[1] - b[1]];
const add = (a, b) => [a[0] + b[0], a[1] + b[1]];
const scale = (a, s) => [a[0] * s, a[1] * s];
const len2 = (a) => Math.hypot(a[0], a[1]);
const unit = (a) => { const l = len2(a) || 1; return [a[0] / l, a[1] / l]; };
const leftPerp = (d) => [-d[1], d[0]];
const cross2 = (a, b) => a[0] * b[1] - a[1] * b[0];
const dot2 = (a, b) => a[0] * b[0] + a[1] * b[1];

/** Per-wall effective thickness (metres). wall.thickness is the W1 opt-in
 *  per-wall override; absent/invalid falls back to the LIVE global binding
 *  ([LOCK-W-08] — read at call time, never captured). Always > 0. */
export function effectiveThickness(w) {
  const t = w ? w.thickness : null;
  return typeof t === "number" && isFinite(t) && t > 0 ? t : WALL_THICKNESS;
}

const isArcWall = (w) =>
  !!w && w.type === "arc" && Array.isArray(w.center) && typeof w.radius === "number" && w.radius > 0;

/** THE mitre primitive. Intersection of the two offset lines through v+offVecA
 *  along dirA and v+offVecB along dirB, generalized to UNEQUAL offsets, with
 *  the existing miter-limit discipline. Returns { p, beveled, parallel, pA, pB }:
 *   - |cross(dirA,dirB)| < EPS_CROSS ⇒ parallel lines. p = midpoint of the two
 *     plain offset points (on the transverse line through v). This single rule
 *     covers: anti-parallel reversal → transverse butt point (equal offsets ⇒
 *     exactly v); parallel continuation, equal offsets → the shared offset
 *     point (pA==pB bitwise ⇒ midpoint == it); unequal offsets → the step-butt
 *     locus at the node's transverse line (square step — thickness changes AT
 *     the node). beveled=true iff the offsets actually differ.
 *   - finite intersection farther than limit = max(|offA|,|offB|)·MITER_LIMIT_RATIO
 *     ⇒ bevel to the midpoint of the two plain offsets (bounded, beveled=true).
 *   - otherwise the true mitre point (beveled=false).
 *  Bit-for-bit compatible with the former wallAuthor.mitreJoint for equal
 *  offsets (its adversarial pins prove the delegation). */
export function offsetLineIntersect(v, dirA, offVecA, dirB, offVecB, opts = {}) {
  const pA = [v[0] + offVecA[0], v[1] + offVecA[1]];
  const pB = [v[0] + offVecB[0], v[1] + offVecB[1]];
  const mid = [(pA[0] + pB[0]) / 2, (pA[1] + pB[1]) / 2];
  const den = cross2(dirA, dirB);
  if (Math.abs(den) < EPS_CROSS) {
    const sep = Math.hypot(pA[0] - pB[0], pA[1] - pB[1]);
    return { p: mid, beveled: sep > VERTEX_EPS, parallel: true, pA, pB };
  }
  const t = cross2(sub(pB, pA), dirB) / den;
  const m = [pA[0] + dirA[0] * t, pA[1] + dirA[1] * t];
  const limit = opts.limit != null
    ? opts.limit
    : Math.max(len2(offVecA), len2(offVecB)) * MITER_LIMIT_RATIO;
  if (!isFinite(m[0]) || !isFinite(m[1]) || len2(sub(m, v)) > limit + 1e-9)
    return { p: mid, beveled: true, parallel: false, pA, pB };
  return { p: m, beveled: false, parallel: false, pA, pB };
}

/* ── Content fingerprint (memo key) ─────────────────────────────────────────
 * getWallGraph's fingerprint + per-wall (thickness ?? '') + live WALL_THICKNESS
 * + arc center/radius/ccw. Shared by getWallJoins AND getWallPolygons so the
 * two memos always agree on "did geometry change?". */
export function joinFingerprint(walls) {
  const list = walls || [];
  let key = list.length + "|" + jointTol() + "|" + WALL_THICKNESS + "|";
  for (const w of list) {
    if (!w || !Array.isArray(w.start) || !Array.isArray(w.end)) continue;
    key += w.id + ":" + w.start[0] + "," + w.start[1] + ";" + w.end[0] + "," + w.end[1] + ";" +
      (w.type || "") + (w.style || "") + ";" + (w.thickness != null ? w.thickness : "") + ";" +
      (isArcWall(w) ? w.center[0] + "," + w.center[1] + "," + w.radius + "," + (w.ccw ? 1 : 0) : "") + "|";
  }
  return key;
}

/* ── Through-crossings (X case ii) ──────────────────────────────────────────
 * Two straight walls crossing mid-mid have NO graph node (no endpoint within
 * tol of anything). Detect interior straight-straight crossings; deterministic
 * host = longer wall (tie → smaller id lexicographic). wallPolygons emits the
 * other wall as two parts butting the host's faces.
 *
 * OWNERSHIP (W1-gate QA ruling — replaces the old endpoint-proximity skip): a
 * crossing is skipped ONLY when the graph TRULY owns that intersection — some
 * node incident to BOTH walls (endpoint cluster, wallGraph §1, or derived
 * T-split, wallGraph §2) COINCIDES with the crossing along at least one of
 * the two walls (< tol along-wall). That node's fan (tee/cross join) is then
 * the SINGLE owner; emitting a record too would double-trim. The old
 * heuristic ("skip anything nearer than tol to any of the four endpoints")
 * assumed the graph owns every near-end hit — FALSE: the graph clusters
 * ENDPOINTS only, so an unowned near-end crossing got no node, no T-split AND
 * no record (both walls emitted full rectangles — an unowned-overlap dead
 * band at wall ends). Interior test is now purely metric (1 nm — endpoint
 * TOUCHES are not crossings); everything else is decided by ownership.
 * Coincidence is measured with the graph's OWN stored data (split.t /
 * endpoint node ids), so the handoff boundary is bit-for-bit the graph's own
 * T-split boundary: below it the node fan trims, above it the crossing record
 * trims — no band where neither owner claims it, no band where both do.
 * Pure + order-independent (s/t are exact under operand swap: IEEE
 * negation/division; the crossing point is computed from the
 * deterministically-chosen host and the shared-node test is pair-symmetric). */
export function segmentCrossings(walls) {
  const list = (walls || []).filter(
    (w) => w && Array.isArray(w.start) && Array.isArray(w.end) && w.type !== "arc"
  );
  const tol = jointTol();
  const graph = getWallGraph(walls);
  // wallId → node ids where the wall participates (endpoint-cluster nodes +
  // every node that T-splits it). A node owns a crossing of (a,b) iff it is in
  // BOTH sets and sits within tol of the crossing point.
  const nodeSets = new Map();
  list.forEach((w) => {
    const info = graph.walls.get(w.id);
    if (!info) return;
    const s = new Set([info.startNode, info.endNode]);
    (info.splits || []).forEach((sp) => s.add(sp.nodeId));
    nodeSets.set(w.id, s);
  });
  // Does node `nid` coincide with a crossing at arc-length tCross along wall w?
  // Uses the graph's own data (endpoint node ids, stored split.t) — never a
  // re-derived distance — so ownership can NEVER disagree with the graph about
  // whether a junction exists there (no float skew at the tol boundary).
  const alongOwns = (info, L, tCross, nid) => {
    if (!info) return false;
    if (info.startNode === nid && tCross < tol) return true;
    if (info.endNode === nid && L - tCross < tol) return true;
    return (info.splits || []).some((sp) => sp.nodeId === nid && Math.abs(sp.t - tCross) < tol);
  };
  const out = [];
  for (let i = 0; i < list.length; i++) {
    for (let j = i + 1; j < list.length; j++) {
      const a = list[i], b = list[j];
      const va = sub(a.end, a.start), vb = sub(b.end, b.start);
      const La = len2(va), Lb = len2(vb);
      if (La < 0.01 || Lb < 0.01) continue; // MIN_WALL_LEN parity with wallGraph
      const den = cross2(va, vb);
      if (Math.abs(den) < EPS_CROSS * La * Lb) continue; // parallel
      const w0 = sub(b.start, a.start);
      const s = cross2(w0, vb) / den; // param along a (0..1)
      const t = cross2(w0, va) / den; // param along b (0..1)
      if (s * La <= 1e-9 || (1 - s) * La <= 1e-9) continue; // endpoint touch, not a crossing
      if (t * Lb <= 1e-9 || (1 - t) * Lb <= 1e-9) continue;
      let host = a, tHost = s, tOther = t, hv = va;
      if (Lb > La || (Lb === La && String(b.id) < String(a.id))) {
        host = b; tHost = t; tOther = s; hv = vb;
      }
      const other = host === a ? b : a;
      const p = [host.start[0] + hv[0] * tHost, host.start[1] + hv[1] * tHost];
      // TRUE ownership: a node shared by BOTH walls that coincides with the
      // crossing along at least one of them (a shared node within tol of both
      // centerlines is geometrically within ~2·tol of the crossing, so
      // one-wall coincidence suffices; near-parallel far crossings fail
      // coincidence on both walls and are correctly emitted).
      const sa = nodeSets.get(a.id), sb = nodeSets.get(b.id);
      const infoA = graph.walls.get(a.id), infoB = graph.walls.get(b.id);
      let owned = false;
      if (sa && sb) {
        for (const nid of sa) {
          if (!sb.has(nid)) continue;
          if (alongOwns(infoA, La, s * La, nid) || alongOwns(infoB, Lb, t * Lb, nid)) {
            owned = true; break;
          }
        }
      }
      if (owned) continue; // the node fan trims there — exactly one owner
      out.push({ p, hostId: host.id, otherId: other.id, tHost, tOther });
    }
  }
  out.sort((x, y) =>
    x.hostId < y.hostId ? -1 : x.hostId > y.hostId ? 1 :
    x.otherId < y.otherId ? -1 : x.otherId > y.otherId ? 1 : x.tHost - y.tHost);
  return out;
}

/* ── Arc helpers (endpoint tangents + band param) ── */
function arcParams(w) {
  const c = w.center;
  const sweep = arcSweep(w);
  const angA = Math.atan2(w.start[1] - c[1], w.start[0] - c[0]);
  return { c, r: w.radius, sweep, angA };
}

/** Outward unit direction of a wall-end (node → wall body). Straight walls use
 *  their own segment direction; arcs use the endpoint TANGENT along travel. */
function outwardDir(w, end) {
  if (isArcWall(w)) {
    const ap = arcParams(w);
    if (!isFinite(ap.sweep) || Math.abs(ap.sweep) < 1e-6) return null;
    const sgn = ap.sweep >= 0 ? 1 : -1;
    if (end === "start") {
      return unit([-Math.sin(ap.angA) * sgn, Math.cos(ap.angA) * sgn]);
    }
    const th = ap.angA + ap.sweep;
    return unit([Math.sin(th) * sgn, -Math.cos(th) * sgn]);
  }
  const d = sub(w.end, w.start);
  const L = len2(d);
  if (L < EPS_LEN) return null;
  return end === "start" ? [d[0] / L, d[1] / L] : [-d[0] / L, -d[1] / L];
}

/** Along-sweep parameter of a point (0 at start, 1 at end), unwrap-tolerant for
 *  points slightly BEFORE the start (they alias to ~2π/|sweep|). */
function arcTParam(pt, ap) {
  const TWO = Math.PI * 2;
  const th = Math.atan2(pt[1] - ap.c[1], pt[0] - ap.c[0]);
  const norm = (x, ref) => { let d = x - ref; while (d < 0) d += TWO; return d; };
  let t = ap.sweep >= 0 ? norm(th, ap.angA) / ap.sweep : -norm(ap.angA, th) / ap.sweep;
  const full = TWO / Math.abs(ap.sweep);
  if (full > 1.5 && t > full - 0.25) t -= full;
  return t;
}

/** Nearest-to-ref intersection of the line q0 + t·e (e unit) with the circle
 *  (c, R); null when the line misses. Closed form (quadratic). */
function lineCircleNearest(q0, e, c, R, ref) {
  const fx = q0[0] - c[0], fz = q0[1] - c[1];
  const b = fx * e[0] + fz * e[1];
  const disc = b * b - (fx * fx + fz * fz - R * R);
  if (disc < 0) return null;
  const s = Math.sqrt(disc);
  const cand = [-b - s, -b + s].map((t) => [q0[0] + e[0] * t, q0[1] + e[1] * t]);
  return len2(sub(cand[0], ref)) <= len2(sub(cand[1], ref)) ? cand[0] : cand[1];
}

function radialArcCap(ap, h, end) {
  const th = end === "start" ? ap.angA : ap.angA + ap.sweep;
  const outR = ap.r + h, inR = Math.max(0.001, ap.r - h); // arcBandGeometry radii discipline
  const t = end === "start" ? 0 : 1;
  return {
    outer: [ap.c[0] + outR * Math.cos(th), ap.c[1] + outR * Math.sin(th)],
    inner: [ap.c[0] + inR * Math.cos(th), ap.c[1] + inR * Math.sin(th)],
    tOuter: t, tInner: t,
  };
}

/* ── The fan ──────────────────────────────────────────────────────────────── */
let _joinsMemo = { key: null, val: null };

/** getWallJoins(walls) → Map<nodeId, { nodeId, p, kind, ends }>.
 *  kind: 'free' | 'continuation' | 'L' | 'T' | 'X' | 'multi' (by slot count;
 *  2-slot nodes split by the wallGraph collinearity rule, ≤2° off straight).
 *  ends[k] = { wallId, end:'start'|'end'|'mid', through, dirOut, halfThickness,
 *              angleToNextDeg, vLeft, vRight, cap, trim:{left,right}, beveled,
 *              arcCap?, fallback? }.
 *  - vLeft/vRight are the SHARED wedge-vertex arrays (adjacent ends reference
 *    the same float64s — assert with toBe, not toBeCloseTo).
 *  - cap = the end's polygon cap polyline in LOCAL order (local-left →
 *    local-right, i.e. exactly the CCW traversal wallPolygons splices in).
 *  - trim = signed distance along dirOut from the node to where each face is
 *    cut (0 at a square/step cap, +h_host at a tee branch, ±h at an L).
 *  - through slots (end:'mid') are the DERIVED host passes at tee/cross nodes;
 *    the host polygon itself stays unbroken (host face wins).
 *  Single-slot content-fingerprint memo (getWallGraph pattern). */
export function getWallJoins(walls) {
  const key = joinFingerprint(walls);
  if (_joinsMemo.key === key) return _joinsMemo.val;
  const val = buildWallJoins(walls || []);
  _joinsMemo = { key, val };
  return val;
}

function buildWallJoins(walls) {
  const graph = getWallGraph(walls);
  const byId = new Map();
  (walls || []).forEach((w) => { if (w && graph.walls.has(w.id)) byId.set(w.id, w); });
  const joins = new Map();

  graph.nodes.forEach((node) => {
    const p = [node.p[0], node.p[1]];
    const slots = [];
    node.ends.forEach((e) => {
      const w = byId.get(e.wallId);
      if (!w) return;
      const d = outwardDir(w, e.end);
      if (!d) return;
      // OWN-AXIS ANCHOR (2026-09-15 immutable-authoring contract). node.p is
      // the CENTROID of the clustered endpoints; the graph clusters endpoints
      // up to jointTol() apart, and drawn legs deliberately meet with their
      // offset endpoints ≤ t apart (each leg is its own reference offset —
      // nothing is re-mitred at commit). A wall's REAL faces run parallel to
      // its OWN centerline through its OWN endpoint, so every wall-end slot's
      // face lines are anchored there — never at the centroid (anchoring at
      // node.p skewed both walls' faces by half the endpoint gap: the host's
      // interior dimension read 894 for a 1000 wall). Coincident endpoints
      // keep the shared `p` array (byte-identical fan, exact vertex sharing).
      const own = e.end === "start" ? w.start : w.end;
      const anchor = Math.hypot(own[0] - p[0], own[1] - p[1]) <= ANCHOR_EPS ? p : [own[0], own[1]];
      slots.push({
        wallId: e.wallId, end: e.end, dirOut: d,
        halfThickness: effectiveThickness(w) / 2,
        through: false, isArc: isArcWall(w), wall: w, anchor,
      });
    });
    // Derived host through-slots at tee/cross nodes (wallGraph's T-splits).
    graph.walls.forEach((info, wid) => {
      if (!info.splits || !info.splits.some((s) => s.nodeId === node.id)) return;
      const w = byId.get(wid);
      if (!w || isArcWall(w)) return; // arc chords are never T-split (wallGraph §2)
      const h = effectiveThickness(w) / 2;
      // OFF-AXIS RE-ANCHOR (W1-gate QA ruling). node.p is the endpoint-cluster
      // centroid and the graph accepts it up to jointTol() OFF this host's
      // centerline (wallGraph §2: dist(foot, node.p) < tol). The host's REAL
      // faces run parallel to the CENTERLINE, so the through-slot face lines
      // are anchored at the PROJECTION of the node onto it — never at node.p
      // (anchoring at node.p offset the derived host face lines by the full
      // off-axis distance: branch caps floated off / buried into the host).
      // When the projection IS the node point we keep the shared `p` array so
      // the on-axis fan arithmetic stays byte-identical (see faceOff below).
      const tA = (p[0] - w.start[0]) * info.dir[0] + (p[1] - w.start[1]) * info.dir[1];
      const foot = [w.start[0] + info.dir[0] * tA, w.start[1] + info.dir[1] * tA];
      const anchor = foot[0] === p[0] && foot[1] === p[1] ? p : foot; // ONE shared float64 anchor for both slots
      slots.push({ wallId: wid, end: "mid", dirOut: [info.dir[0], info.dir[1]], halfThickness: h, through: true, isArc: false, wall: w, anchor });
      slots.push({ wallId: wid, end: "mid", dirOut: [-info.dir[0], -info.dir[1]], halfThickness: h, through: true, isArc: false, wall: w, anchor });
    });

    slots.forEach((s) => { s.ang = Math.atan2(s.dirOut[1], s.dirOut[0]); });
    slots.sort((a, b) => a.ang - b.ang ||
      (a.wallId + ":" + a.end < b.wallId + ":" + b.end ? -1 : 1)); // stable dup-angle tie-break
    const n = slots.length;
    if (n === 0) { joins.set(node.id, { nodeId: node.id, p, kind: "free", ends: [] }); return; }

    const ends = [];
    if (n === 1) {
      const s = slots[0];
      const lp = leftPerp(s.dirOut);
      const vLeft = add(p, scale(lp, s.halfThickness));
      const vRight = add(p, scale(lp, -s.halfThickness));
      ends.push({
        wallId: s.wallId, end: s.end, through: false, dirOut: s.dirOut,
        halfThickness: s.halfThickness, angleToNextDeg: 360,
        vLeft, vRight, cap: [vLeft, vRight],
        trim: { left: 0, right: 0 }, beveled: false,
      });
      if (s.isArc) ends[0].arcCap = radialArcCap(arcParams(s.wall), s.halfThickness, s.end); // free arc end: radial by design (no fallback flag)
    } else {
      // Wedge v_ij between each CCW-adjacent pair: i's LEFT face ∩ j's RIGHT face.
      // faceOff = offset vector (relative to the fan origin p) of slot s's
      // left(+1)/right(−1) face line, which runs through
      // s.anchor + sign·h·leftPerp(d). Every wall-END slot (and every on-axis
      // through slot) has anchor === p — that branch is the ORIGINAL
      // expression, byte-identical fan. Off-axis through slots add the
      // node→centerline shift (QA off-axis-tee ruling) so the derived host
      // face lines are the host's REAL faces. The default miter limit then
      // measures |offVec| INCLUDING the shift — deliberately: the limit must
      // never shrink below the true face-line offset, or legitimate off-axis
      // tee mitres (up to tol from node.p) would spuriously bevel.
      const faceOff = (s, sign) => {
        const perp = scale(leftPerp(s.dirOut), sign * s.halfThickness);
        return s.anchor === p ? perp : add(sub(s.anchor, p), perp);
      };
      const wedges = slots.map((s, k) => {
        const j = slots[(k + 1) % n];
        return offsetLineIntersect(p, s.dirOut, faceOff(s, 1), j.dirOut, faceOff(j, -1));
      });
      // Junction-core coverage rule: at a node where 3+ walls all END (no
      // through host body — a pure multi-way star, unlike a tee/cross whose
      // host rectangle covers the core), seam-to-seam caps would leave the
      // core polygon uncovered (a GAP — mandate violation). Standard CAD
      // treatment (Revit/ArchiCAD n-way junctions): PIE wedges — every cap
      // runs through the SHARED node point, producing the familiar cross
      // seams. 2-slot nodes never need it (the node is always the midpoint of
      // the mitre seam — the two wedge vertices are point-reflections through
      // it), and through-host nodes must NOT do it (the pie would stab into
      // the host body). The node point `p` is one shared array across all
      // ends — the same exact-vertex guarantee as the wedge vertices.
      const pieNode = n >= 3 && !slots.some((s) => s.through);
      slots.forEach((s, k) => {
        const vL = wedges[k];                 // shared with CCW-next
        const vR = wedges[(k - 1 + n) % n];   // shared with CCW-prev
        const lp = leftPerp(s.dirOut);
        // own plain offsets sit on the slot's REAL face lines (anchor-based;
        // anchor === p everywhere except off-axis through slots — see above)
        const pOwnL = add(s.anchor, scale(lp, s.halfThickness));
        const pOwnR = add(s.anchor, scale(lp, -s.halfThickness));
        // Cap corner rule (local-left → local-right traversal):
        //  mitre        → the shared wedge vertex;
        //  parallel     → own plain offset (square step / butt at the node's
        //                 transverse line — inserting the midpoint would violate
        //                 the thinner wall's thickness), except equal offsets
        //                 where the midpoint IS the shared flow-through point;
        //  angular bevel→ own plain corner + shared bevel midpoint (n-gon).
        const cornerPts = (info, own, side) => {
          if (!info.beveled) return [info.p];
          if (info.parallel) return [own];
          return side === "left" ? [own, info.p] : [info.p, own];
        };
        const cap = [
          ...cornerPts(vL, pOwnL, "left"),
          ...(pieNode ? [p] : []),
          ...cornerPts(vR, pOwnR, "right"),
        ];
        const ccwGap = ((slots[(k + 1) % n].ang - s.ang) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
        ends.push({
          wallId: s.wallId, end: s.end, through: s.through, dirOut: s.dirOut,
          halfThickness: s.halfThickness,
          angleToNextDeg: (ccwGap * 180) / Math.PI,
          vLeft: vL.p, vRight: vR.p, cap,
          trim: {
            left: dot2(sub(cap[0], p), s.dirOut),
            right: dot2(sub(cap[cap.length - 1], p), s.dirOut),
          },
          beveled: !!(vL.beveled || vR.beveled),
        });
        // Arc node-trim: 2-slot nodes only (tangent-mitre seam → line-circle caps).
        // Tee/cross arcs are DEFERRED (consistent with wallGraph's no-T-split-for-
        // arcs rule) → pinned radial-cap fallback, never silent.
        if (s.isArc) {
          const entry = ends[ends.length - 1];
          const ap = arcParams(s.wall);
          const h = s.halfThickness;
          let cap2 = null;
          if (n === 2 && !vL.parallel && !vL.beveled && !vR.parallel && !vR.beveled) {
            const seam = sub(vR.p, vL.p);
            if (len2(seam) > EPS_LEN) {
              const e = unit(seam);
              const po = lineCircleNearest(vL.p, e, ap.c, ap.r + h, p);
              const pi = lineCircleNearest(vL.p, e, ap.c, Math.max(0.001, ap.r - h), p);
              if (po && pi) {
                const to = arcTParam(po, ap), ti = arcTParam(pi, ap);
                const isStart = s.end === "start";
                const ok = (t) => isFinite(t) && (isStart ? t >= -0.1 && t <= 0.5 : t >= 0.5 && t <= 1.1);
                if (ok(to) && ok(ti)) cap2 = { outer: po, inner: pi, tOuter: to, tInner: ti };
              }
            }
          }
          if (cap2) entry.arcCap = cap2;
          else { entry.arcCap = radialArcCap(ap, h, s.end); entry.fallback = "radial-cap"; }
        }
      });
    }

    let kind;
    if (n === 1) kind = "free";
    else if (n === 2) {
      const angBetween = (Math.acos(Math.max(-1, Math.min(1, dot2(slots[0].dirOut, slots[1].dirOut)))) * 180) / Math.PI;
      kind = Math.abs(180 - angBetween) <= COLLINEAR_DEG ? "continuation" : "L";
    } else if (n === 3) kind = "T";
    else if (n === 4) kind = "X";
    else kind = "multi";

    joins.set(node.id, { nodeId: node.id, p, kind, ends });
  });

  return joins;
}
